import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Plus, Trash2, Loader2, RefreshCw, Send, Printer,
  Download, FileText, CheckCircle2, Lock, ArrowLeft, Sparkles,
  Wand2, RotateCcw, AlertTriangle, ExternalLink,
} from "lucide-react";
import StrategyWhitePaperModal from "./StrategyWhitePaperModal";

/**
 * Strategy Planner — per-client tab.
 *
 * Two modes:
 *   1. LIST: index of strategies + "New strategy" button
 *   2. EDITOR: inline editor for a single strategy — cover meta,
 *      objectives, sections, "Refresh live data" (pulls the previous
 *      period from every connected service), workflow controls
 *      (draft → in_review → client_review), share link, PDF export,
 *      and a "Preview client view" button that opens the rendered
 *      client-facing HTML in a new tab.
 */
const STATUS_STYLES = {
  draft:         { label: "DRAFT",         bg: "bg-zinc-200 text-zinc-800" },
  in_review:     { label: "OPS REVIEW",    bg: "bg-blue-100 text-blue-800" },
  client_review: { label: "CLIENT REVIEW", bg: "bg-amber-100 text-amber-800" },
  approved:      { label: "APPROVED",      bg: "bg-emerald-100 text-emerald-800" },
  locked:        { label: "SIGNED & LOCKED", bg: "bg-purple-100 text-purple-800" },
};

const PERIOD_LABELS = {
  monthly: "Monthly", quarterly: "Quarterly",
  biannual: "Biannual", annual: "Annual",
};

export default function ClientStrategyPlannerPage() {
  const { clientId } = useParams();
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);   // strategy_id or null
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newPeriodType, setNewPeriodType] = useState("quarterly");
  const [newPeriodEnd, setNewPeriodEnd] = useState(() =>
    new Date().toISOString().slice(0, 10));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/clients/${clientId}/strategies`);
      setRows(data.strategies || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load strategies");
    } finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const createStrategy = async () => {
    setCreating(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/strategies`, {
        period_type: newPeriodType,
        period_end: newPeriodEnd,
      });
      toast.success("Strategy created");
      await load();
      setSelected(data.strategy.strategy_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    } finally { setCreating(false); }
  };

  if (selected) {
    return <StrategyEditor clientId={clientId} strategyId={selected}
      onBack={() => { setSelected(null); load(); }} />;
  }

  return (
    <div className="p-6 space-y-6 max-w-7xl" data-testid="strategy-planner-page">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-sm bg-[#002FA7]/10 grid place-items-center">
          <FileText className="h-5 w-5 text-[#002FA7]" />
        </div>
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">
            Strategy Planner
          </h1>
          <p className="text-xs text-zinc-500 mt-1">
            Draft &amp; ship monthly, quarterly, biannual, or annual strategy
            documents. Ops drafts → Ops reviews → Client reviews → Client
            signs (locked). Live data auto-pulls from AHREFS, Google Search
            Console, Bing and other connected services for the previous period.
          </p>
        </div>
      </div>

      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-4 flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-[180px]">
            <Label className="label-overline">Period type</Label>
            <Select value={newPeriodType} onValueChange={setNewPeriodType}>
              <SelectTrigger className="mt-1 rounded-sm text-[12px] h-9"
                data-testid="strategy-period-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(PERIOD_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[180px]">
            <Label className="label-overline">Target period end</Label>
            <Input type="date" value={newPeriodEnd}
              onChange={(e) => setNewPeriodEnd(e.target.value)}
              className="mt-1 rounded-sm text-[12px] h-9"
              data-testid="strategy-period-end" />
          </div>
          <Button onClick={createStrategy} disabled={creating}
            className="rounded-sm bg-[#002FA7] hover:bg-[#001F70] text-white"
            data-testid="new-strategy-btn">
            {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <><Plus className="h-3.5 w-3.5 mr-1.5" /> New strategy</>}
          </Button>
        </CardContent>
      </Card>

      <div>
        <div className="label-overline mb-2">Existing strategies</div>
        {loading ? (
          <div className="p-8 text-center text-sm text-zinc-500">
            <Loader2 className="h-4 w-4 animate-spin inline mr-1" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-sm text-zinc-500 border border-dashed
            border-zinc-300 rounded-sm">
            No strategies yet — create one above.
          </div>
        ) : (
          <div className="space-y-2">
            {rows.map((s) => {
              const st = STATUS_STYLES[s.status] || STATUS_STYLES.draft;
              return (
                <button key={s.strategy_id}
                  onClick={() => setSelected(s.strategy_id)}
                  data-testid={`strategy-row-${s.strategy_id}`}
                  className="w-full text-left rounded-sm border border-zinc-200
                    bg-white p-3 hover:border-[#002FA7] hover:shadow-sm
                    transition-all">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[9px] font-mono px-1.5 py-0.5
                      rounded-sm ${st.bg}`}>{st.label}</span>
                    <span className="font-display font-bold text-sm">
                      {s.title}
                    </span>
                    <span className="text-[10px] font-mono text-zinc-500">
                      · {PERIOD_LABELS[s.period_type]} · covers previous {s.period_type}
                    </span>
                    <span className="ml-auto text-[10px] font-mono text-zinc-400">
                      Updated {new Date(s.updated_at).toLocaleString()}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function StrategyEditor({ clientId, strategyId, onBack }) {
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wpOpen, setWpOpen] = useState(false);
  const [wp, setWp] = useState(null);   // white_paper status object
  const wpPollRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/clients/${clientId}/strategies/${strategyId}`);
      setDoc(data);
      setWp(data?.white_paper || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  }, [clientId, strategyId]);
  useEffect(() => { load(); }, [load]);

  // Poll /whitepaper/status every 4s while a run is in flight. Stops
  // automatically when status transitions to complete/failed/not_started.
  const RUNNING_STATUSES = React.useMemo(() => new Set(
    ["queued", "phase_1", "phase_2", "phase_3", "phase_4", "phase_5", "phase_6"]
  ), []);
  useEffect(() => {
    const isRunning = wp?.status && RUNNING_STATUSES.has(wp.status);
    if (!isRunning) {
      if (wpPollRef.current) { clearInterval(wpPollRef.current); wpPollRef.current = null; }
      return undefined;
    }
    const tick = async () => {
      try {
        const { data } = await api.get(
          `/clients/${clientId}/strategies/${strategyId}/whitepaper/status`);
        setWp(data.white_paper || null);
      } catch (_) { /* silent — will retry next tick */ }
    };
    tick();
    wpPollRef.current = setInterval(tick, 4000);
    return () => {
      if (wpPollRef.current) { clearInterval(wpPollRef.current); wpPollRef.current = null; }
    };
  }, [wp?.status, RUNNING_STATUSES, clientId, strategyId]);

  const resetWhitepaper = async () => {
    if (!window.confirm("Clear this white paper and reset for a fresh run?")) return;
    try {
      await api.post(
        `/clients/${clientId}/strategies/${strategyId}/whitepaper/reset`);
      setWp(null);
      toast.success("White paper cleared");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    }
  };

  const save = async () => {
    setBusy("save");
    try {
      await api.patch(`/clients/${clientId}/strategies/${strategyId}`, {
        title: doc.title,
        objectives: doc.objectives,
        sections: doc.sections,
        cover_meta: doc.cover_meta,
      });
      toast.success("Saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(null); }
  };

  const refreshData = async () => {
    setBusy("refresh");
    try {
      const { data } = await api.post(
        `/clients/${clientId}/strategies/${strategyId}/refresh-data`);
      toast.success(`Pulled ${data.row_count} live-data rows`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refresh failed");
    } finally { setBusy(null); }
  };

  const changeStatus = async (newStatus) => {
    setBusy(newStatus);
    try {
      await api.post(
        `/clients/${clientId}/strategies/${strategyId}/status`,
        { status: newStatus });
      toast.success(`Moved to ${newStatus.replace("_", " ")}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Status change failed");
    } finally { setBusy(null); }
  };

  const downloadPdf = async () => {
    setBusy("pdf");
    try {
      const res = await api.post(
        `/clients/${clientId}/strategies/${strategyId}/pdf`,
        {}, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${doc.title}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "PDF generation failed");
    } finally { setBusy(null); }
  };

  const updateSection = (idx, patch) => {
    setDoc((d) => ({
      ...d, sections: d.sections.map((s, i) =>
        i === idx ? { ...s, ...patch } : s),
    }));
  };

  const addSection = () => {
    setDoc((d) => ({
      ...d,
      sections: [...(d.sections || []), {
        id: `sec_${Date.now()}`,
        title: "New section",
        content_html: "<p>Content...</p>",
        kind: "text",
      }],
    }));
  };

  const removeSection = (idx) => {
    if (!window.confirm("Remove this section?")) return;
    setDoc((d) => ({
      ...d, sections: d.sections.filter((_, i) => i !== idx),
    }));
  };

  const rewriteTone = async (idx) => {
    const s = doc.sections[idx];
    if (!s?.content_html?.trim()) {
      toast.error("Add some content first"); return;
    }
    setBusy(`tone-${idx}`);
    try {
      const { data } = await api.post(
        `/clients/${clientId}/strategies/${strategyId}/rewrite-tone`,
        { content_html: s.content_html, section_title: s.title });
      updateSection(idx, { content_html: data.content_html });
      toast.success("Rewritten in strategy voice — review & save");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rewrite failed");
    } finally { setBusy(null); }
  };

  if (loading || !doc) {
    return <div className="p-8 text-sm text-zinc-500">
      <Loader2 className="h-4 w-4 animate-spin inline mr-1" /> Loading…
    </div>;
  }
  const st = STATUS_STYLES[doc.status] || STATUS_STYLES.draft;
  const locked = doc.status === "locked";
  const shareUrl = `${window.location.origin}/strategy/share/${doc.share_token}`;

  return (
    <div className="p-6 space-y-4 max-w-6xl" data-testid="strategy-editor">
      <div className="flex items-center gap-2 flex-wrap">
        <Button variant="ghost" size="sm" onClick={onBack} className="rounded-sm">
          <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back to list
        </Button>
        <span className={`text-[10px] font-mono px-2 py-0.5 rounded-sm ${st.bg}`}>
          {st.label}
        </span>
        <div className="ml-auto flex items-center gap-1.5 flex-wrap">
          <Button size="sm" variant="outline" onClick={refreshData}
            disabled={busy !== null || locked}
            className="rounded-sm" data-testid="strategy-refresh-data">
            {busy === "refresh"
              ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
              : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
            Refresh live data
          </Button>
          <Button size="sm" variant="outline"
            onClick={() => window.open(shareUrl + "?print=1", "_blank")}
            className="rounded-sm" data-testid="strategy-print">
            <Printer className="h-3.5 w-3.5 mr-1" /> Print preview
          </Button>
          <Button size="sm" variant="outline" onClick={downloadPdf}
            disabled={busy === "pdf"} className="rounded-sm"
            data-testid="strategy-download-pdf">
            {busy === "pdf"
              ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
              : <Download className="h-3.5 w-3.5 mr-1" />}
            PDF
          </Button>
          <Button size="sm" onClick={save} disabled={busy !== null || locked}
            className="rounded-sm bg-[#002FA7] hover:bg-[#001F70] text-white"
            data-testid="strategy-save">
            {busy === "save"
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : "Save changes"}
          </Button>
        </div>
      </div>

      {/* Workflow controls */}
      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-3 flex items-center gap-2 flex-wrap">
          <span className="text-[11px] text-zinc-500">Workflow:</span>
          {["draft", "in_review", "client_review"].map((k) => (
            <Button key={k} size="sm"
              variant={doc.status === k ? "default" : "outline"}
              onClick={() => changeStatus(k)}
              disabled={busy !== null || locked}
              className="rounded-sm text-[11px] h-7"
              data-testid={`strategy-status-${k}`}>
              {k === "in_review" ? "Move to Ops Review" :
                k === "client_review" ? "Send to Client" : "Back to Draft"}
            </Button>
          ))}
          {doc.status === "client_review" && (
            <span className="text-[10px] text-amber-800 bg-amber-50
              px-2 py-1 rounded-sm ml-2">
              Client can now view + approve via the share link below
            </span>
          )}
          {locked && (
            <span className="text-[10px] text-purple-800 bg-purple-50
              px-2 py-1 rounded-sm ml-2">
              <Lock className="h-3 w-3 inline mr-1" />
              Signed by {doc.signed_name} on {new Date(doc.signed_at).toLocaleString()}
            </span>
          )}
        </CardContent>
      </Card>

      {/* Share link */}
      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-3">
          <Label className="label-overline">Client share link</Label>
          <div className="mt-1 flex gap-2">
            <Input value={shareUrl} readOnly
              className="rounded-sm font-mono text-[11px] h-8"
              data-testid="strategy-share-url"
              onClick={(e) => e.target.select()} />
            <Button size="sm" variant="outline"
              onClick={() => {
                navigator.clipboard.writeText(shareUrl);
                toast.success("Copied");
              }} className="rounded-sm">Copy</Button>
          </div>
          <p className="text-[10px] text-zinc-500 mt-1">
            Only accessible once you&apos;ve moved the strategy to
            &ldquo;Client review&rdquo; or beyond.
          </p>
        </CardContent>
      </Card>

      {/* Title + Objectives */}
      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-4 space-y-3">
          <div>
            <Label className="label-overline">Title</Label>
            <Input value={doc.title || ""}
              onChange={(e) => setDoc({ ...doc, title: e.target.value })}
              className="mt-1 rounded-sm text-[13px]"
              data-testid="strategy-title" disabled={locked} />
          </div>
          <div>
            <Label className="label-overline">Client objectives</Label>
            <Textarea value={doc.objectives || ""}
              onChange={(e) => setDoc({ ...doc, objectives: e.target.value })}
              placeholder="What does the client want to achieve this period? (business goals, target metrics, brand priorities)"
              className="mt-1 rounded-sm text-[12px] min-h-[80px]"
              data-testid="strategy-objectives" disabled={locked} />
          </div>
        </CardContent>
      </Card>

      {/* Live data snapshot summary */}
      {doc.live_data_snapshot && (
        <Card className="rounded-sm border-emerald-200 bg-emerald-50/40">
          <CardContent className="p-3">
            <div className="text-[11px] text-emerald-800">
              <CheckCircle2 className="h-3 w-3 inline mr-1" />
              Live data snapshot from{" "}
              <strong>{doc.live_data_snapshot.period_start}</strong> to{" "}
              <strong>{doc.live_data_snapshot.period_end}</strong> —
              {" "}{(doc.live_data_snapshot.live_sources || []).length} rows,
              captured {new Date(doc.live_data_snapshot.generated_at).toLocaleString()}
            </div>
          </CardContent>
        </Card>
      )}

      {/* SEO White Paper pipeline panel */}
      <WhitePaperPanel wp={wp} doc={doc}
        clientId={clientId} strategyId={strategyId}
        onOpen={() => setWpOpen(true)} onReset={resetWhitepaper} />

      {wpOpen && (
        <StrategyWhitePaperModal clientId={clientId} strategy={doc}
          onClose={() => setWpOpen(false)}
          onStarted={() => {
            setWp({ status: "queued", progress_note: "Queued…" });
          }} />
      )}

      {/* Sections editor */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="label-overline">Sections</div>
          <Button size="sm" variant="outline" onClick={addSection}
            disabled={locked} className="rounded-sm"
            data-testid="strategy-add-section">
            <Plus className="h-3 w-3 mr-1" /> Add section
          </Button>
        </div>
        <div className="space-y-2">
          {(doc.sections || []).map((s, i) => (
            <Card key={s.id} className="rounded-sm border-zinc-200"
              data-testid={`strategy-section-${i}`}>
              <CardContent className="p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <Badge className="rounded-sm text-[9px] font-mono
                    bg-zinc-100 text-zinc-700 border-zinc-200">
                    #{i + 1}
                  </Badge>
                  <Input value={s.title || ""}
                    onChange={(e) => updateSection(i, { title: e.target.value })}
                    className="rounded-sm font-display font-bold text-[13px] flex-1"
                    disabled={locked} />
                  <Button size="sm" variant="outline"
                    onClick={() => rewriteTone(i)}
                    disabled={locked || busy !== null}
                    className="rounded-sm text-[11px] h-7 border-amber-300
                      text-amber-800 hover:bg-amber-50"
                    data-testid={`strategy-tone-btn-${i}`}
                    title="Rewrite this section in Iceberg's growth voice">
                    {busy === `tone-${i}`
                      ? <Loader2 className="h-3 w-3 animate-spin" />
                      : <><Sparkles className="h-3 w-3 mr-1" /> Positive tone</>}
                  </Button>
                  <Button size="icon" variant="ghost"
                    onClick={() => removeSection(i)}
                    disabled={locked}
                    className="h-7 w-7 text-red-500 hover:bg-red-50">
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
                <Textarea value={s.content_html || ""}
                  onChange={(e) => updateSection(i, { content_html: e.target.value })}
                  placeholder="HTML content — headings, tables, lists supported"
                  className="rounded-sm text-[11px] font-mono min-h-[100px]"
                  disabled={locked} />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

const PHASE_LABELS = {
  not_started: "Not started",
  queued: "Queued — waiting to start",
  phase_1: "Phase 1 / 6 — SEO Audit (Opus)",
  phase_2: "Phase 2 / 6 — 8-Post Content Plan (Opus)",
  phase_3: "Phase 3 / 6 — Writing 8 Blog Posts (Sonnet)",
  phase_4: "Phase 4 / 6 — Linking Architecture (Opus)",
  phase_5: "Phase 5 / 6 — Campaign Plan (Opus)",
  phase_6: "Phase 6 / 6 — HTML Assembly (Opus)",
  complete: "Complete — white paper ready",
  failed: "Failed",
};

const PHASE_PROGRESS = {
  queued: 3, phase_1: 12, phase_2: 25, phase_3: 55,
  phase_4: 70, phase_5: 82, phase_6: 92, complete: 100, failed: 100,
};

function WhitePaperPanel({ wp, doc, clientId, strategyId, onOpen, onReset }) {
  const status = wp?.status || "not_started";
  const running = ["queued", "phase_1", "phase_2", "phase_3",
                   "phase_4", "phase_5", "phase_6"].includes(status);
  const complete = status === "complete";
  const failed = status === "failed";
  const notStarted = status === "not_started" || !wp;
  const locked = doc?.status === "locked";
  const progress = PHASE_PROGRESS[status] ?? 0;

  const shareUrl = `${window.location.origin}/strategy/share/${doc.share_token}?wp=1`;

  // Blog post progress dots for Phase 3.
  const postProgress = wp?.phase_3_progress || [];

  return (
    <Card className="rounded-sm border-2 border-[#002FA7]/20 bg-blue-50/30"
      data-testid="whitepaper-panel">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-[#002FA7]" />
              <div className="font-display font-bold text-sm">
                SEO White Paper
              </div>
              <Badge variant="outline" className="text-[9px] font-mono
                rounded-sm bg-white">
                6-PHASE CLAUDE PIPELINE
              </Badge>
            </div>
            <p className="text-[11px] text-zinc-600 mt-1 leading-relaxed max-w-2xl">
              Full print-ready white paper: SEO audit · 8-post content plan · 8 written blog posts ·
              linking architecture · campaign plan · assembled HTML. Planning phases run
              on Claude Opus, blog writing on Sonnet.
            </p>
          </div>
          <div className="flex items-center gap-1.5">
            {notStarted && (
              <Button size="sm" onClick={onOpen} disabled={locked}
                className="rounded-sm bg-[#002FA7] hover:bg-[#001F70] text-white"
                data-testid="whitepaper-open-btn">
                <Wand2 className="h-3.5 w-3.5 mr-1.5" />
                Generate white paper
              </Button>
            )}
            {(complete || failed) && (
              <>
                {complete && (
                  <>
                    <Button size="sm" variant="outline"
                      onClick={() => window.open(shareUrl, "_blank")}
                      className="rounded-sm" data-testid="whitepaper-preview-btn">
                      <ExternalLink className="h-3.5 w-3.5 mr-1" /> Preview HTML
                    </Button>
                  </>
                )}
                <Button size="sm" variant="outline" onClick={onReset}
                  className="rounded-sm border-red-300 text-red-700 hover:bg-red-50"
                  data-testid="whitepaper-reset-btn">
                  <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reset
                </Button>
              </>
            )}
          </div>
        </div>

        {running && (
          <div className="space-y-2" data-testid="whitepaper-progress">
            <div className="flex items-center justify-between text-[11px]">
              <span className="font-mono uppercase tracking-wider text-[#002FA7]">
                {PHASE_LABELS[status]}
              </span>
              <span className="font-mono text-zinc-500">{progress}%</span>
            </div>
            <div className="h-1.5 bg-white rounded-sm overflow-hidden border border-zinc-200">
              <div className="h-full bg-[#002FA7] transition-all duration-1000"
                style={{ width: `${progress}%` }} />
            </div>
            {wp?.progress_note && (
              <div className="text-[11px] text-zinc-600 italic">
                <Loader2 className="h-3 w-3 inline mr-1 animate-spin" />
                {wp.progress_note}
              </div>
            )}
            {status === "phase_3" && (
              <div className="flex items-center gap-1 mt-1">
                <span className="text-[10px] font-mono text-zinc-500 mr-1">POSTS:</span>
                {Array.from({ length: 8 }).map((_, i) => {
                  const state = postProgress[i];
                  const cls = state === "done" ? "bg-emerald-500"
                            : state === "failed" ? "bg-red-500"
                            : "bg-zinc-300 animate-pulse";
                  return (
                    <span key={`post-slot-${i}`}
                      className={`inline-block w-6 h-2 rounded-sm ${cls}`}
                      data-testid={`whitepaper-post-status-${i + 1}`}
                      title={`Post ${i + 1}: ${state || "in progress"}`} />
                  );
                })}
              </div>
            )}
          </div>
        )}

        {complete && (
          <div className="flex items-center gap-2 text-[11px] text-emerald-800
            bg-emerald-50 border border-emerald-200 rounded-sm p-2">
            <CheckCircle2 className="h-3.5 w-3.5" />
            White paper generated
            {wp?.completed_at && (
              <> at <strong>{new Date(wp.completed_at).toLocaleString()}</strong></>
            )}
            {" "}· {wp?.phase_6_html_length
              ? `${Math.round(wp.phase_6_html_length / 1024)}KB of HTML`
              : "ready"} · use Preview HTML to open, PDF button up top to download.
          </div>
        )}

        {failed && (
          <div className="text-[11px] text-red-800 bg-red-50 border
            border-red-200 rounded-sm p-2 space-y-1"
            data-testid="whitepaper-error-panel">
            <div className="flex items-center gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5" />
              <strong>Pipeline failed</strong>
              {wp?.failed_at && (
                <> at {new Date(wp.failed_at).toLocaleString()}</>
              )}
            </div>
            {wp?.error && (
              <div className="font-mono text-[10px] mt-1 leading-relaxed">
                {wp.error}
              </div>
            )}
            <div>Click <strong>Reset</strong> above to clear and try again.</div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

